import { parseVoicePreferences } from './preferences';
// Voice mode's shared client state (BUILD 2 C2-8): the one view of the voice
// and read-aloud configuration and of whether voice mode is on, for WP2-7
// (read-aloud: shown when the person's `readAloud` is on, at their `rate`),
// WP2-8 (the controller, the bar and the settings) and WP2-9 (a summary is
// spoken only in voice mode with read-aloud on; spoken approvals need the
// policy and the person's switch).
//
// The blocks come from GET /api/v1/speech/config (the call the SpeechProvider
// also reads for its limits). SpeechProvider loads them for the company/person
// and drops them on scope change; the settings sections refresh them after their
// own writes, so a preference or policy written in one surface reaches the
// others. Each block is checked structurally and fails closed to null
// (unavailable) when it is missing or malformed, as `fetchConfig` does for
// the deployment modes. useVoiceBarActive (voice-bar.tsx) is the composer's
// view of `voiceMode`.
import { useSyncExternalStore } from 'react';
import type { SpeechConfig } from '@/speech/contracts';
import { fetchSpeechConfig } from '@/speech/transport/http';
import { setLaneTimingEnabled } from './lane-timing';
import { parseFullDuplex } from './full-duplex';
import { parseProcessors } from './processor-disclosure';
import type {
  ReadAloudConfig,
  SpeechPolicy,
  VoiceConfig,
  VoiceConfigState,
  VoiceConfigStore,
  VoiceModeState,
  VoicePrefs,
} from './contracts';

const NOT_LOADED: VoiceConfigState = Object.freeze({ voice: null, readAloud: null, speech: null });

/** Retain same-person preferences while every speech permission awaits a fresh policy. */
function awaitingPolicy(state: VoiceConfigState): VoiceConfigState {
  if (!state.voice) return NOT_LOADED;
  return {
    voice: {
      ...state.voice,
      available: false,
      processors: [],
      handsFree: { available: false, reason: 'unknown' },
      fullDuplex: { available: false, reason: 'unknown', platforms: [] },
    },
    readAloud: null,
    speech: null,
  };
}

type Json = Record<string, unknown>;
const object = (value: unknown): Json | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Json) : null;
const bool = (value: unknown) => value === true;
const reason = (value: unknown) => (typeof value === 'string' && value !== '' ? value : null);
const number = (value: unknown, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

function parseWindows(value: unknown, fallback: { endpoint: number }) {
  const windows = object(value);
  return windows
    ? {
        endpoint: number(windows.endpoint, fallback.endpoint),
      }
    : fallback;
}

function parsePolicy(value: unknown): SpeechPolicy {
  const policy = object(value) ?? {};
  return {
    voiceMode: bool(policy.voiceMode),
    readAloud: bool(policy.readAloud),
    spokenApprovals: bool(policy.spokenApprovals),
  };
}

/** The `voice` block, or null (voice mode unavailable) when it is missing or malformed. */
export function parseVoiceConfig(value: unknown): VoiceConfig | null {
  const voice = object(value);
  const handsFree = object(voice?.handsFree);
  const idle = object(voice?.idleExitSec);
  const processors = parseProcessors(voice?.processors);
  if (
    !voice ||
    typeof voice.available !== 'boolean' ||
    !handsFree ||
    !processors ||
    (voice.available && !processors.length)
  )
    return null;
  const undoMs = parseWindows(voice.undoMs, { endpoint: 2000 });
  const prefs = parseVoicePreferences(voice.prefs, undoMs);
  if (!prefs) return null;
  return {
    processors,
    available: voice.available,
    reason: reason(voice.reason),
    handsFree: { available: bool(handsFree.available), reason: reason(handsFree.reason) },
    fullDuplex: parseFullDuplex(voice.fullDuplex),
    undoMs,
    idleExitSec: { handsfree: number(idle?.handsfree, 30) },
    policy: parsePolicy(voice.policy),
    prefs,
    laneTiming: bool(voice.laneTiming),
  };
}

/** The `readAloud` block, or null when it is missing or malformed. */
export function parseReadAloudConfig(value: unknown): ReadAloudConfig | null {
  const block = object(value);
  const rate = object(block?.rate);
  const processors = parseProcessors(block?.processors);
  if (
    !block ||
    typeof block.available !== 'boolean' ||
    !processors ||
    (block.available && !processors.length)
  )
    return null;
  return {
    processors,
    available: block.available,
    reason: reason(block.reason),
    rate: { min: number(rate?.min, 0.8), max: number(rate?.max, 1.5) },
  };
}

export type LoadableVoiceConfigStore = VoiceConfigStore & {
  /** The active company/person key, invalidated on reset. */
  scope(): string;
  /** Sign-out: nothing is loaded until the next person's refresh. */
  reset(): void;
  /** A preference write's answer, applied before the refresh confirms it. */
  adoptPrefs(prefs: VoicePrefs): void;
};

export function createVoiceConfigStore(
  load: () => Promise<SpeechConfig> = () => fetchSpeechConfig(),
): LoadableVoiceConfigStore {
  let state: VoiceConfigState = NOT_LOADED;
  let version = 0;
  let activeScope = '';
  const listeners = new Set<() => void>();
  const publish = (next: VoiceConfigState) => {
    state = next;
    for (const listener of [...listeners]) listener();
  };
  return {
    current: () => state,
    scope: () => activeScope,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    async refresh(scopeKey = activeScope) {
      const asked = ++version;
      const pending = scopeKey === activeScope ? awaitingPolicy(state) : NOT_LOADED;
      activeScope = scopeKey;
      publish(pending);
      try {
        const config = await load();
        if (asked !== version || activeScope !== scopeKey) return;
        publish({
          voice: parseVoiceConfig(config?.voice),
          readAloud: parseReadAloudConfig(config?.readAloud),
          speech: config,
        });
      } catch {
        if (asked === version && activeScope === scopeKey) publish(NOT_LOADED);
      }
    },
    reset() {
      version += 1;
      activeScope = '';
      if (state !== NOT_LOADED) publish(NOT_LOADED);
    },
    adoptPrefs(prefs) {
      version += 1;
      if (state.voice) publish({ ...state, voice: { ...state.voice, prefs } });
    },
  };
}

export const voiceConfigStore = createVoiceConfigStore();
voiceConfigStore.subscribe(() =>
  setLaneTimingEnabled(voiceConfigStore.current().voice?.laneTiming === true),
);

export type SettableVoiceModeState = VoiceModeState & { set(active: boolean): void };

export function createVoiceModeState(): SettableVoiceModeState {
  let active = false;
  const listeners = new Set<() => void>();
  return {
    active: () => active,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    set(next) {
      if (next === active) return;
      active = next;
      for (const listener of [...listeners]) listener();
    },
  };
}

export const voiceMode: SettableVoiceModeState = createVoiceModeState();

/** The voice and read-aloud blocks, for a component. */
export const useVoiceConfig = (): VoiceConfigState =>
  useSyncExternalStore(voiceConfigStore.subscribe, voiceConfigStore.current, () => NOT_LOADED);

/** Whether voice mode is on, for a component. */
export const useVoiceModeActive = (): boolean =>
  useSyncExternalStore(voiceMode.subscribe, voiceMode.active, () => false);
